import { useQuery } from "@tanstack/react-query";
import client from "@phrmpc/infrastructure/phrmpc/axios";
import { resolveAttachmentContent } from "@phrmpc/shared/utils/resolveAttachmentContent";

type UseDamlAttachmentContentParams = {
  dailyActivityMonitoringDetailId: string;
  fileId: string;
  fileName: string;
  enabled?: boolean;
};

export function useDamlAttachmentContent({
  dailyActivityMonitoringDetailId,
  fileId,
  fileName,
  enabled = true,
}: UseDamlAttachmentContentParams) {
  return useQuery({
    queryKey: [
      "daml-attachment-content",
      dailyActivityMonitoringDetailId,
      fileId,
    ],
    queryFn: async () => {
      const response = await client.get(
        `/deployment/daily-activity-monitoring-log-headers/${dailyActivityMonitoringDetailId}/files/${fileId}/content`,
        { responseType: "blob" },
      );
      const resolved = await resolveAttachmentContent(response, fileName);
      return URL.createObjectURL(resolved.blob);
    },
    enabled:
      enabled && Boolean(dailyActivityMonitoringDetailId) && Boolean(fileId),
    staleTime: Infinity,
  });
}

// Keyed by fileId - mirrors the hook's own `staleTime: Infinity`/queryKey shape, so a preview
// opened twice for the same file doesn't refetch the blob or mint a second object URL.
const damlAttachmentUrlCache = new Map<string, Promise<string>>();

/**
 * Plain-function counterpart to `useDamlAttachmentContent`, for use as a `ControlledFileUploader`
 * `getFileUrl` callback - those are called lazily on preview/download click, not rendered, so they
 * can't call a React Query hook.
 */
export function resolveDamlAttachmentUrl(
  dailyActivityMonitoringDetailId: string,
  fileId: string,
  fileName: string,
): Promise<string> {
  const cached = damlAttachmentUrlCache.get(fileId);
  if (cached) return cached;

  const promise = client
    .get(
      `/deployment/daily-activity-monitoring-log-headers/${dailyActivityMonitoringDetailId}/files/${fileId}/content`,
      { responseType: "blob" },
    )
    .then(async (response) => {
      const resolved = await resolveAttachmentContent(response, fileName);
      return URL.createObjectURL(resolved.blob);
    });

  damlAttachmentUrlCache.set(fileId, promise);
  promise.catch(() => damlAttachmentUrlCache.delete(fileId));
  return promise;
}
